// src/components/templates/diary/DiaryUIComponents.jsx
import React from 'react';

// --- CONSTANTS / ENUMS ---
export const MeetingStatus = {
  PLANNED: 'PLANNED',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED'
};

export const MinuteType = {
  NOTE: 'NOTE',
  DECISION: 'DECISION',
  TASK: 'TASK'
};

export const NoteCategory = {
  GENERAL: 'GENERAL',
  IDEA: 'IDEA',
  UPDATE: 'UPDATE',
  TASK_UPDATE: 'TASK_UPDATE'
};

export const DiaryDomain = {
  PERSONAL: 'PERSONAL',
  WORK: 'WORK',
  HEALTH: 'HEALTH',
  FINANCE: 'FINANCE',
  SOCIAL: 'SOCIAL',
  IDEAS: 'IDEAS'
};

export const ContactCategory = {
  FAMILY: 'FAMILY',
  FRIEND: 'FRIEND',
  WORK: 'WORK',
  SERVICE: 'SERVICE',
  OTHER: 'OTHER'
};

export const ViewState = {
  DASHBOARD: 'DASHBOARD',
  GOALS: 'GOALS',
  MEETINGS: 'MEETINGS',
  MEETING_DETAIL: 'MEETING_DETAIL',
  CALENDAR: 'CALENDAR',
  NOTES: 'NOTES',
  CONTACTS: 'CONTACTS',
  JOURNAL: 'JOURNAL',
  PLANNER: 'PLANNER',
  RESOURCES: 'RESOURCES',
  RECORDS: 'RECORDS',
  LEARNING: 'LEARNING',
  TOOLS: 'TOOLS',
  SETTINGS: 'SETTINGS'
};

// --- CARD COMPONENT ---
export const Card = ({ children, className = '', onClick }) => (
  <div 
    className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// --- BUTTON COMPONENT ---
export const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md',
  style={},
  disabled = false, 
  className = '',
  type = 'button'
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-md hover:shadow-lg',
    secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-indigo-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      style={style}
    >
      {children}
    </button>
  );
};

// --- BADGE COMPONENT ---
export const Badge = ({ children, color = 'gray', className = '' }) => {
  const colors = {
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    pink: 'bg-pink-100 text-pink-700 border-pink-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colors[color]} ${className}`}>
      {children}
    </span>
  );
};

// --- INPUT COMPONENT ---
export const Input = ({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  className = '',
  disabled = false,
  error,
  ...props 
}) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
        error ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
      } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
      {...props}
    />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

// --- SELECT COMPONENT ---
export const Select = ({ label, options = [], value, onChange, className = '', disabled = false }) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
        disabled ? 'bg-gray-50 cursor-not-allowed' : ''
      }`}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// --- TEXTAREA COMPONENT ---
export const TextArea = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  rows = 4, 
  className = '',
  disabled = false 
}) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none ${
        disabled ? 'bg-gray-50 cursor-not-allowed' : ''
      }`}
    />
  </div>
);

// --- MODAL COMPONENT ---
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        <div className={`relative bg-white rounded-2xl shadow-xl transform transition-all w-full ${sizes[size]} mx-auto`}>
          {title && (
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
          )}
          <div className="px-6 py-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

// --- TABS COMPONENT ---
export const Tabs = ({ tabs, activeTab, onChange, className = '' }) => (
  <div className={`flex border-b border-gray-200 ${className}`}>
    {tabs.map(tab => (
      <button
        key={tab.key}
        onClick={() => onChange(tab.key)}
        className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
          activeTab === tab.key
            ? 'border-indigo-600 text-indigo-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
      >
        {tab.icon && <tab.icon className="w-4 h-4 mr-2" />}
        {tab.label}
        {tab.count !== undefined && (
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            activeTab === tab.key ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
          }`}>
            {tab.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// --- LOADING SPINNER ---
export const Spinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <svg className={`animate-spin ${sizes[size]} ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
};

// --- EMPTY STATE ---
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    {Icon && (
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-gray-400" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-gray-700 mb-1">{title}</h3>
    {description && <p className="text-sm text-gray-500 mb-4 max-w-sm">{description}</p>}
    {action}
  </div>
);

// --- AVATAR ---
export const Avatar = ({ name, size = 'md', color, className = '' }) => {
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-xl',
  };

  const initial = name ? name.charAt(0).toUpperCase() : '?';

  return (
    <div className={`${sizes[size]} rounded-full flex items-center justify-center font-bold text-white ${color || 'bg-indigo-500'} ${className}`}>
      {initial}
    </div>
  );
};

// --- TOOLTIP (simple) ---
export const Tooltip = ({ children, content, position = 'top' }) => {
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <div className="relative group">
      {children}
      <div className={`absolute ${positions[position]} hidden group-hover:block z-50`}>
        <div className="bg-gray-900 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap">
          {content}
        </div>
      </div>
    </div>
  );
};

export default {
  Card,
  Button,
  Badge,
  Input,
  Select,
  TextArea,
  Modal,
  Tabs,
  Spinner,
  EmptyState,
  Avatar,
  Tooltip,
  MeetingStatus,
  MinuteType,
  NoteCategory,
  DiaryDomain,
  ContactCategory,
  ViewState
};